import Cookies from "universal-cookie";
import { apiAgent } from "../../axiosConfig";
import { getCookieSetOptions } from "../../config/cookiesSettings";
import { checkTeamContext, notifyTeamChanged, navigateToTeam } from "../../teamContext/teamSwitchService";
import { safeDestination } from "../../teamContext/routes";
import { queryClient } from "../../queries/queryClient";
import { profileCache } from "../../components/utils/CacheUtils";
import {
    teamlLoadError,
    updateCurrentTeam,
    updateTeamLoadingStage,
    clearCurrentTeamAccess,
    teamLoadTransientError,
    teamSwitchStarted, teamSwitchFailed, resetTeamSwitch,
} from "../../redux/currentTeamSlice";

let latestCurrentTeamRequest = 0;

export const fetchCurrentTeam = (team_id, userValidation) => async (_dispatch) => {
    const requestId = ++latestCurrentTeamRequest;
    _dispatch(updateTeamLoadingStage(true));
    _dispatch(clearCurrentTeamAccess());

    const requestTeamId = String(team_id);
    const requestApiKey = new Cookies().get("api_key");
    const requestIsCurrent = () => {
        const cookies = new Cookies();
        const activeTeamId = cookies.get("team_id");
        if (requestId !== latestCurrentTeamRequest || cookies.get("api_key") !== requestApiKey) return false;
        if (activeTeamId && String(activeTeamId) === requestTeamId) return true;
        // A staff tab can change teams before its first lookup has finished.
        checkTeamContext(requestTeamId);
        return false;
    };

    return apiAgent({
        method: "get",
        url: "/team_members/team",
        params: {
            team_id: team_id,
        },
    })
        .then(async (res) => {
            // Fetch in parallel, but validate the session before applying the team.
            await userValidation;
            if (!requestIsCurrent()) return false;
            if (!res.data.team) {
                _dispatch(teamlLoadError());
                return false;
            }
            let expiring = (!res.data.team.premium && res.data.team.verified && res.data.team.trial_expiring_on_days >= 0 && res.data.team.trial_expiring_on_days <= 30)
            _dispatch(updateCurrentTeam({...res.data.team, expiring: expiring}));
            return true;
        })
        .catch(async (error) => {
            const isTeamUnavailable = [401, 403, 404, 422].includes(error.response?.status);
            // Authenticate before removing the cookie so it is not mistaken for a cross-tab change.
            if (isTeamUnavailable) await userValidation;
            if (!requestIsCurrent()) return false;
            if (isTeamUnavailable) _dispatch(teamlLoadError());
            else _dispatch(teamLoadTransientError("Unable to load your team. Please try again."));
            return false;
        })
        .finally(() => {
            // Discarding a response still finishes its load; a newer request owns its own loader.
            if (requestId === latestCurrentTeamRequest) _dispatch(updateTeamLoadingStage(false));
        });
};

// Resolves true when selection and navigation start; false when blocked or failed.
// fetchTeam loads details in the current document; other selections reload.
export const switchCurrentTeam = (team, { destination = "/feed", fetchTeam = false } = {}) => async (_dispatch, getState) => {
    const switching = getState().current_team.teamSwitch;
    if (!team?.id || (switching && !switching.error)) return false;

    const cookies = new Cookies();
    const user = getState().current_user.user;
    const isStaff = user.is_super_member === true;
    const path = safeDestination(destination);
    const previousTeamId = cookies.get("team_id"), apiKey = cookies.get("api_key");
    _dispatch(teamSwitchStarted({ teamId: team.id, destination: path }));
    try {
        if (apiKey !== user.api_key) {
            navigateToTeam("/login");
            return false;
        }
        if (isStaff) {
            queryClient.clear();
            profileCache.clear();
        }
        // Cleanup may notify subscribers; preserve a newer selection/session.
        if (previousTeamId !== cookies.get("team_id") || apiKey !== cookies.get("api_key")) {
            _dispatch(resetTeamSwitch());
            checkTeamContext();
            return false;
        }
        cookies.set("team_id", team.id, getCookieSetOptions());
        if (isStaff) notifyTeamChanged(team.id, user.id);
        if (fetchTeam) _dispatch(fetchCurrentTeam(team.id));
        navigateToTeam(path, !fetchTeam);
        if (fetchTeam) _dispatch(resetTeamSwitch());
        return true;
    } catch (error) {
        _dispatch(teamSwitchFailed("We couldn’t switch teams. Please try again."));
        return false;
    }
};
